import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Tooltip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { OpenInFull } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import AxiosInstance from "../../services/networkServices";
import routePaths from "../../router/route-paths";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/common";
import { useSidebar } from "../../context/SidebarContext";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../utils/roleAccess";
import fitnessSVG from "../../assets/fitnessSVG.svg";
import foodSVG from "../../assets/foodSvg.svg";
import { listAudios, updateAudio } from "../../http/audio";
import { listKaraoke } from "../../http/karaoke";
import { listDance } from "../../http/dance";
import "./calendar.css";
import PageLayout from "../../components/PageLayout";
import CalendarToolbar from "./CalendarToolbar";
import CalendarGrid from "./CalendarGrid";
import EditEventDialog from "./EditEventDialog";
import AddChallengeDialog from "./AddChallengeDialog";
import DemographicsDialog from "./DemographicsDialog";
import { ROW_HEIGHT, ROW_GAP, DATE_HEADER_HEIGHT, getCategoryPriority, getQuarterFromDate, getQuarterStart, getQuarterEnd, hexToRgba, getEventStatus } from "./calendarUtils";


const Calendar = () => {
  const [challenges, setChallenges] = useState([]);
  const [contests, setContests] = useState([]);
  const [karaokes, setKaraokes] = useState([]);
  const [dances, setDances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentQuarter, setCurrentQuarter] = useState(() => getQuarterFromDate(dayjs()));
  const [showChallenges, setShowChallenges] = useState(true);
  const [showContests, setShowContests] = useState(true);
  const [showKaraoke, setShowKaraoke] = useState(true);
  const [showDance, setShowDance] = useState(true);
  const [showRegistrations, setShowRegistrations] = useState(true);
  const gridRef = useRef(null);
  const [cellWidth, setCellWidth] = useState(0);
  const { isCollapsed } = useSidebar();
  
  // User registrations data
  const [registrationsByDate, setRegistrationsByDate] = useState({});
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Audio assignment state
  const [allAudios, setAllAudios] = useState([]);
  const [selectedAudioIds, setSelectedAudioIds] = useState([]);
  const [originalAudioIds, setOriginalAudioIds] = useState([]);
  
  // Add challenge modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [addFormData, setAddFormData] = useState({
    title: "",
    categoryId: "",
    startDate: "",
    endDate: "",
  });
  
  // Drag state for resizing and moving bars
  const [dragState, setDragState] = useState(null);
  // dragState shape: { type: 'resize-start'|'resize-end'|'move', event, originalStart, originalEnd, currentStart, currentEnd, offsetX, offsetY }
  
  // Demographics modal state
  const [demographicsModalOpen, setDemographicsModalOpen] = useState(false);
  const [selectedDemographicsDate, setSelectedDemographicsDate] = useState(null);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Restricted admin accounts: skip admin-only data and hide activity counts
  // (the API omits them too; this keeps the bars from showing "(0)").
  const { isAdmin, canViewActivity, hasFeature } = usePermissions();
  const countOrNull = (value) => (canViewActivity ? value || 0 : null);
  const canSeeContests = hasFeature(PERMISSIONS.CONTESTS);

  // Fetch challenges
  const fetchChallenges = useCallback(async () => {
    try {
      const response = await AxiosInstance.post("/category/challenge/list", {
        page: 1,
        limit: 500,
        sortBy: "startDate",
        sortOrder: 1,
      });
      if (response.data.status) {
        setChallenges(response.data.data.list);
      }
    } catch (error) {
      console.error("Error fetching challenges:", error);
    }
  }, []);

  // Fetch contests
  const fetchContests = useCallback(async () => {
    try {
      const response = await AxiosInstance.get("/contest?page=1&limit=500");
      if (response.data.status) {
        setContests(response.data.data.list);
      }
    } catch (error) {
      console.error("Error fetching contests:", error);
    }
  }, []);

  // Fetch categories for add challenge modal
  const fetchCategories = useCallback(async () => {
    try {
      const response = await AxiosInstance.post("/category/list", {
        page: 1,
        limit: 100,
      });
      if (response.data.status) {
        setCategories(response.data.data.list);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  // Fetch karaoke songs
  const fetchKaraokes = useCallback(async () => {
    try {
      const response = await listKaraoke();
      if (response?.data?.karaokes) {
        setKaraokes(response.data.karaokes);
      }
    } catch (error) {
      console.error("Error fetching karaokes:", error);
    }
  }, []);

  // Fetch dance tracks
  const fetchDances = useCallback(async () => {
    try {
      const response = await listDance({ limit: 100 });
      if (response?.data?.dances) {
        setDances(response.data.dances);
      }
    } catch (error) {
      console.error("Error fetching dances:", error);
    }
  }, []);

  // Fetch user registrations and group by date (store user data for demographics)
  const fetchUserRegistrations = useCallback(async () => {
    try {
      const response = await AxiosInstance.post("/admin/list-users", {
        page: 1,
        limit: 5000, // Fetch a large batch to get registration data
      });
      if (response.data?.data?.list) {
        const grouped = {};
        response.data.data.list.forEach((user) => {
          if (user.createdAt) {
            const dateKey = dayjs(user.createdAt).format("YYYY-MM-DD");
            if (!grouped[dateKey]) {
              grouped[dateKey] = [];
            }
            grouped[dateKey].push({
              age: user.age,
              gender: user.gender,
            });
          }
        });
        setRegistrationsByDate(grouped);
      }
    } catch (error) {
      console.error("Error fetching user registrations:", error);
    }
  }, []);

  // Calculate demographics for a specific date
  const getDemographicsForDate = useCallback((dateKey) => {
    const users = registrationsByDate[dateKey] || [];
    const total = users.length;
    
    // Gender distribution - handle various formats
    const genderCounts = { male: 0, female: 0, other: 0, unknown: 0 };
    users.forEach((user) => {
      const gender = user.gender?.toString().trim().toLowerCase();
      if (!gender) {
        genderCounts.unknown++;
      } else if (gender === "male" || gender === "m" || gender === "man") {
        genderCounts.male++;
      } else if (gender === "female" || gender === "f" || gender === "woman") {
        genderCounts.female++;
      } else {
        genderCounts.other++;
      }
    });
    
    // Age distribution
    const ageCounts = {
      "13-17": 0,
      "18-24": 0,
      "25-34": 0,
      "35-44": 0,
      "45+": 0,
      "unknown": 0,
    };
    users.forEach((user) => {
      const age = parseInt(user.age) || 0;
      if (age === 0) ageCounts.unknown++;
      else if (age >= 13 && age <= 17) ageCounts["13-17"]++;
      else if (age >= 18 && age <= 24) ageCounts["18-24"]++;
      else if (age >= 25 && age <= 34) ageCounts["25-34"]++;
      else if (age >= 35 && age <= 44) ageCounts["35-44"]++;
      else if (age >= 45) ageCounts["45+"]++;
      else ageCounts.unknown++;
    });
    
    return { total, genderCounts, ageCounts };
  }, [registrationsByDate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      // Registrations and dance tracks are admin-only APIs; contests need the
      // contests permission. A restricted account skips them instead of
      // tripping the 401 session-expiry handler.
      await Promise.all([
        fetchChallenges(),
        canSeeContests ? fetchContests() : Promise.resolve(),
        fetchCategories(),
        isAdmin ? fetchUserRegistrations() : Promise.resolve(),
        fetchKaraokes(),
        isAdmin ? fetchDances() : Promise.resolve(),
      ]);
      setLoading(false);
    };
    fetchData();
  }, [fetchChallenges, fetchContests, fetchCategories, fetchUserRegistrations, fetchKaraokes, fetchDances, isAdmin, canSeeContests]);

  // Measure cell width - recalc on sidebar collapse/expand and after layout paint
  useEffect(() => {
    const updateCellWidth = () => {
      if (gridRef.current) {
        setCellWidth(gridRef.current.offsetWidth / 7);
      }
    };

    // Initial update
    updateCellWidth();

    // Update after sidebar animation completes (300ms transition)
    const timer = setTimeout(updateCellWidth, 350);

    // Update on next paint in case layout shifts
    const raf = requestAnimationFrame(updateCellWidth);

    window.addEventListener("resize", updateCellWidth);
    return () => {
      window.removeEventListener("resize", updateCellWidth);
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [currentQuarter, isCollapsed]);

  // Generate calendar days for the entire quarter (3 months)
  const calendarDays = useMemo(() => {
    const quarterStart = getQuarterStart(currentQuarter.year, currentQuarter.quarter);
    const quarterEnd = getQuarterEnd(currentQuarter.year, currentQuarter.quarter);
    
    // Get the first day of the calendar grid (might be in previous month)
    const calendarStart = quarterStart.startOf("week");
    // Get the last day of the calendar grid (might be in next month)
    const calendarEnd = quarterEnd.endOf("week");
    
    // Get the first month of the quarter (for labeling)
    const firstMonthOfQuarter = quarterStart.month();
    const firstMonthName = quarterStart.format("MMMM");
    
    const days = [];
    let current = calendarStart;
    let firstQuarterDayFound = false;
    let firstMonthLabelShown = false;
    
    while (current.isBefore(calendarEnd) || current.isSame(calendarEnd, "day")) {
      const isInQuarter = (current.isAfter(quarterStart, "day") || current.isSame(quarterStart, "day")) && 
                          (current.isBefore(quarterEnd, "day") || current.isSame(quarterEnd, "day"));
      
      // Check if this is the first day of the quarter (for first month label)
      const isFirstQuarterDay = !firstQuarterDayFound && current.isSame(quarterStart, "day");
      if (isFirstQuarterDay) {
        firstQuarterDayFound = true;
      }
      
      // Check if this is the first month of the quarter and we haven't shown the label yet
      const isFirstMonthOfQuarter = current.month() === firstMonthOfQuarter && !firstMonthLabelShown;
      if (isFirstMonthOfQuarter) {
        firstMonthLabelShown = true;
      }
      
      days.push({ 
        date: current, 
        isCurrentMonth: isInQuarter,
        isMonthStart: current.date() === 1,
        isFirstQuarterDay: isFirstQuarterDay,
        isFirstMonthOfQuarter: isFirstMonthOfQuarter,
        monthName: current.format("MMMM"),
        firstMonthName: isFirstMonthOfQuarter ? firstMonthName : null
      });
      current = current.add(1, "day");
    }
    
    return days;
  }, [currentQuarter]);

  // Helper function to get logo for category
  const getCategoryLogo = (categoryName, defaultLogo) => {
    if (!categoryName) return defaultLogo;
    const categoryLower = categoryName.toLowerCase();
    if (categoryLower.includes("fitness")) {
      return fitnessSVG;
    }
    if (categoryLower.includes("food")) {
      return foodSVG;
    }
    return defaultLogo;
  };

  // Combine and normalize events from challenges and contests
  const allEvents = useMemo(() => {
    const events = [];

    // Add challenges
    if (showChallenges) {
      challenges.forEach((challenge) => {
        events.push({
          id: `challenge-${challenge._id}`,
          rawId: challenge._id,
          title: challenge.title,
          startDate: challenge.startDate,
          endDate: challenge.endDate,
          logo: getCategoryLogo(challenge.category?.name, challenge.category?.logo),
          type: "challenge",
          category: challenge.category?.name,
          categoryId: challenge.categoryId,
          submissionCount: countOrNull(challenge.postCount),
          rawData: challenge,
        });
      });
    }

    // Add contest voting periods
    if (showContests) {
      contests.forEach((contest) => {
        // Determine if contest is currently showing in the app
        const showFromDate = contest.showInCategoriesListFromDate;
        const isCurrentlyShowing = showFromDate 
          ? dayjs().isAfter(dayjs(showFromDate)) && !contest.isDeleted && !contest.isEnded
          : false;
        
        events.push({
          id: `contest-${contest._id}`,
          rawId: contest._id,
          title: `${contest.title} (Voting)`,
          rawTitle: contest.title,
          startDate: contest.votingStartDate,
          endDate: contest.votingEndDate,
          logo: contest.logo,
          type: "contest",
          prize: contest.prize,
          details: contest.details,
          submissionCount: countOrNull(contest.numberOfSubmissions),
          rawData: contest,
          showInCategoriesListFromDate: showFromDate,
          isCurrentlyShowing,
        });
      });
    }

    // Add karaoke songs with availability periods
    if (showKaraoke) {
      karaokes.forEach((karaoke) => {
        // Only show karaokes with availability dates set
        if (karaoke.availabilityStartTime && karaoke.availabilityEndTime) {
          // Format duration from seconds to mm:ss
          const formatDuration = (seconds) => {
            if (!seconds) return "N/A";
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          };
          
          events.push({
            id: `karaoke-${karaoke._id}`,
            rawId: karaoke._id,
            title: karaoke.title,
            artist: karaoke.artist,
            startDate: karaoke.availabilityStartTime,
            endDate: karaoke.availabilityEndTime,
            logo: karaoke.coverImage,
            type: "karaoke",
            duration: karaoke.duration,
            durationFormatted: formatDuration(karaoke.duration),
            submissionCount: countOrNull(karaoke.submissionCount || karaoke.postCount),
            rawData: karaoke,
          });
        }
      });
    }

    // Add dance tracks with availability periods
    if (showDance) {
      dances.forEach((dance) => {
        // Only show dances with availability dates set
        if (dance.availabilityStartTime && dance.availabilityEndTime) {
          // Format duration from seconds to mm:ss
          const formatDuration = (seconds) => {
            if (!seconds) return "N/A";
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          };
          
          events.push({
            id: `dance-${dance._id}`,
            rawId: dance._id,
            title: dance.title,
            startDate: dance.availabilityStartTime,
            endDate: dance.availabilityEndTime,
            logo: dance.coverImage,
            type: "dance",
            duration: dance.duration,
            durationFormatted: formatDuration(dance.duration),
            submissionCount: countOrNull(dance.submissionCount || dance.postCount),
            rawData: dance,
          });
        }
      });
    }

    return events;
  }, [challenges, contests, karaokes, dances, showChallenges, showContests, showKaraoke, showDance, canViewActivity]);

  // Get visible events for current quarter
  const visibleEvents = useMemo(() => {
    const quarterStart = getQuarterStart(currentQuarter.year, currentQuarter.quarter);
    const quarterEnd = getQuarterEnd(currentQuarter.year, currentQuarter.quarter);

    return allEvents.filter((event) => {
      const start = dayjs(event.startDate);
      const end = dayjs(event.endDate);
      return start.isBefore(quarterEnd) && end.isAfter(quarterStart);
    });
  }, [allEvents, currentQuarter]);

  // Assign slots to events based on category hierarchy
  const eventSlots = useMemo(() => {
    // Sort events by:
    // 1. Category hierarchy (fashion first, then makeup, pets, food, fitness)
    // 2. Start date (earlier events first within same category)
    const sortedEvents = [...visibleEvents].sort((a, b) => {
      const priorityA = getCategoryPriority(a);
      const priorityB = getCategoryPriority(b);
      
      // First sort by category priority
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Then by start date
      return dayjs(a.startDate).diff(dayjs(b.startDate));
    });

    const slots = new Map();
    const occupiedSlots = {};

    sortedEvents.forEach((event) => {
      const start = dayjs(event.startDate).startOf("day");
      const end = dayjs(event.endDate).startOf("day");

      // Find the first available slot
      let slot = 0;
      let slotFound = false;

      while (!slotFound) {
        slotFound = true;
        let current = start;

        while (current.isBefore(end) || current.isSame(end, "day")) {
          const dateKey = current.format("YYYY-MM-DD");
          if (occupiedSlots[dateKey]?.has(slot)) {
            slotFound = false;
            slot++;
            break;
          }
          current = current.add(1, "day");
        }
      }

      let current = start;
      while (current.isBefore(end) || current.isSame(end, "day")) {
        const dateKey = current.format("YYYY-MM-DD");
        if (!occupiedSlots[dateKey]) {
          occupiedSlots[dateKey] = new Set();
        }
        occupiedSlots[dateKey].add(slot);
        current = current.add(1, "day");
      }

      slots.set(event.id, slot);
    });

    return slots;
  }, [visibleEvents]);


  // Calculate bar segments for week wrapping
  const getEventBarSegments = useCallback(
    (event) => {
      const originalEnd = dayjs(event.endDate).startOf("day");
      let start = dayjs(event.startDate).startOf("day");
      let end = originalEnd;

      const calendarStart = calendarDays[0].date.startOf("day");
      const calendarEnd = calendarDays[calendarDays.length - 1].date.startOf("day");

      if (start.isBefore(calendarStart)) start = calendarStart;
      if (end.isAfter(calendarEnd)) end = calendarEnd;

      const segments = [];
      let currentStart = start;

      while (currentStart.isBefore(end) || currentStart.isSame(end, "day")) {
        const dayIndex = calendarDays.findIndex((d) =>
          d.date.startOf("day").isSame(currentStart, "day")
        );
        if (dayIndex === -1) break;

        const weekRow = Math.floor(dayIndex / 7);
        const startCol = dayIndex % 7;

        let endCol = startCol;
        let segmentEnd = currentStart;

        // Extend segment until end of week (Saturday = col 6) or end of event
        while (endCol < 6) {
          const nextDay = segmentEnd.add(1, "day");
          // Stop if next day would be after the event end
          if (nextDay.isAfter(end)) break;
          endCol++;
          segmentEnd = nextDay;
        }

        const isStart = currentStart.isSame(
          dayjs(event.startDate).startOf("day"),
          "day"
        );
        // Check if this segment ends on the actual event end date
        const isEnd = segmentEnd.isSame(originalEnd, "day") || 
                      segmentEnd.isSame(end, "day");

        segments.push({ weekRow, startCol, endCol, isStart, isEnd });
        currentStart = segmentEnd.add(1, "day");
      }

      return segments;
    },
    [calendarDays]
  );

  // Calculate max slots per week
  const maxSlotsPerWeek = useMemo(() => {
    const weekSlots = {};
    visibleEvents.forEach((event) => {
      const segments = getEventBarSegments(event);
      const slot = eventSlots.get(event.id) || 0;
      segments.forEach((seg) => {
        weekSlots[seg.weekRow] = Math.max(weekSlots[seg.weekRow] || 0, slot + 1);
      });
    });
    return weekSlots;
  }, [visibleEvents, getEventBarSegments, eventSlots]);

  // Navigation
  const goToPrevQuarter = () => {
    setCurrentQuarter((q) => {
      if (q.quarter === 1) {
        return { year: q.year - 1, quarter: 4 };
      }
      return { year: q.year, quarter: q.quarter - 1 };
    });
  };

  const goToNextQuarter = () => {
    setCurrentQuarter((q) => {
      if (q.quarter === 4) {
        return { year: q.year + 1, quarter: 1 };
      }
      return { year: q.year, quarter: q.quarter + 1 };
    });
  };

  const goToToday = () => {
    setCurrentQuarter(getQuarterFromDate(dayjs()));
  };

  const getRowHeight = (weekIndex) => {
    const slots = maxSlotsPerWeek[weekIndex] || 0;
    // Add extra padding at bottom to accommodate wrapped content in bars
    return DATE_HEADER_HEIGHT + Math.max(slots, 1) * (ROW_HEIGHT + ROW_GAP) + 30;
  };

  // Get quarter label
  const getQuarterLabel = () => {
    const quarterStart = getQuarterStart(currentQuarter.year, currentQuarter.quarter);
    const quarterEnd = getQuarterEnd(currentQuarter.year, currentQuarter.quarter);
    return `${currentQuarter.year} Q${currentQuarter.quarter} (${quarterStart.format("MMM")} - ${quarterEnd.format("MMM")})`;
  };

  // Find month start markers for the current view - including first month of quarter
  const getMonthStartsInWeek = (weekIndex) => {
    const weekDays = calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7);
    const monthStarts = [];
    
    weekDays.forEach((day, idx) => {
      // Show label if it's the 1st of a month, first day of quarter, or first month of quarter
      if (day.isMonthStart || day.isFirstQuarterDay || day.isFirstMonthOfQuarter) {
        // Use the first month name if it's the first month of quarter, otherwise use the month name
        const labelName = day.firstMonthName || day.monthName;
        // Avoid duplicates
        if (!monthStarts.find(m => m.monthName === labelName)) {
          monthStarts.push({
            dayIndex: idx,
            monthName: labelName
          });
        }
      }
    });
    
    return monthStarts;
  };

  // Fetch all audios for assignment
  const fetchAllAudios = async () => {
    try {
      const payload = { page: 1, limit: 500 };
      const res = await listAudios(payload);
      setAllAudios(res?.data?.list || []);
      return res?.data?.list || [];
    } catch (err) {
      console.error('Error fetching audios:', err);
      return [];
    }
  };

  // Handle bar click - open edit modal
  const handleBarClick = async (event, e) => {
    e.stopPropagation();
    setSelectedEvent(event);
    
    if (event.type === "challenge") {
      setEditFormData({
        title: event.title,
        startDate: dayjs(event.startDate).format("YYYY-MM-DDTHH:mm"),
        endDate: dayjs(event.endDate).format("YYYY-MM-DDTHH:mm"),
      });
    } else {
      // For contests, fetch audios and populate form
      const audios = await fetchAllAudios();
      const contest = event.rawData || {};
      const assignedAudioIds = audios
        .filter(audio => audio.contests?.some(c => c._id === contest._id))
        .map(audio => audio._id);
      setSelectedAudioIds(assignedAudioIds);
      setOriginalAudioIds(assignedAudioIds);
      
      setEditFormData({
        title: event.rawTitle || event.title.replace(" (Voting)", ""),
        prize: event.prize || "",
        details: event.details || "",
        votingStartDate: dayjs(event.startDate).format("YYYY-MM-DDTHH:mm"),
        votingEndDate: dayjs(event.endDate).format("YYYY-MM-DDTHH:mm"),
        showInCategoriesListFromDate: event.showInCategoriesListFromDate 
          ? dayjs(event.showInCategoriesListFromDate).format("YYYY-MM-DDTHH:mm")
          : "",
      });
    }
    
    setEditModalOpen(true);
  };

  // Handle form input change
  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save
  const handleSave = async () => {
    if (!selectedEvent) return;
    
    setSaving(true);
    try {
      if (selectedEvent.type === "challenge") {
        const payload = {
          title: editFormData.title,
          startDate: new Date(editFormData.startDate).toISOString(),
          endDate: new Date(editFormData.endDate).toISOString(),
        };
        
        await AxiosInstance.put(`/category/challenge/${selectedEvent.rawId}`, payload);
        dispatch(showToast({ color: "success", msg: "Challenge updated successfully" }));
      } else {
        const payload = {
          id: selectedEvent.rawId,
          title: editFormData.title,
          prize: editFormData.prize || null,
          details: editFormData.details,
          votingStartDate: new Date(editFormData.votingStartDate).toISOString(),
          votingEndDate: new Date(editFormData.votingEndDate).toISOString(),
        };
        
        // Add showInCategoriesListFromDate if provided
        if (editFormData.showInCategoriesListFromDate) {
          payload.showInCategoriesListFromDate = new Date(editFormData.showInCategoriesListFromDate).toISOString();
        }
        
        await AxiosInstance.put("/contest", payload);
        
        // Handle audio assignment changes
        const addedAudioIds = selectedAudioIds.filter(id => !originalAudioIds.includes(id));
        const removedAudioIds = originalAudioIds.filter(id => !selectedAudioIds.includes(id));
        
        // Update audios that were added to this contest
        for (const audioId of addedAudioIds) {
          const audio = allAudios.find(a => a._id === audioId);
          if (audio) {
            const existingContestIds = audio.contests?.map(c => c._id) || [];
            const formData = new FormData();
            formData.append('id', audioId);
            formData.append('name', audio.name);
            // Add existing contests plus this new one
            [...existingContestIds, selectedEvent.rawId].forEach(cId => {
              formData.append('contestIds[]', cId);
            });
            // Keep existing category IDs
            audio.categories?.forEach(cat => {
              formData.append('categoryIds[]', cat._id);
              formData.append(`categoryStatus[${cat._id}]`, cat.status || 'ACTIVE');
            });
            try {
              await updateAudio(formData);
            } catch (err) {
              console.error('Error adding audio to contest:', err);
            }
          }
        }
        
        // Update audios that were removed from this contest
        for (const audioId of removedAudioIds) {
          const audio = allAudios.find(a => a._id === audioId);
          if (audio) {
            const updatedContestIds = (audio.contests?.map(c => c._id) || [])
              .filter(cId => cId !== selectedEvent.rawId);
            const formData = new FormData();
            formData.append('id', audioId);
            formData.append('name', audio.name);
            // Add remaining contests (excluding this one)
            updatedContestIds.forEach(cId => {
              formData.append('contestIds[]', cId);
            });
            // Keep existing category IDs
            audio.categories?.forEach(cat => {
              formData.append('categoryIds[]', cat._id);
              formData.append(`categoryStatus[${cat._id}]`, cat.status || 'ACTIVE');
            });
            try {
              await updateAudio(formData);
            } catch (err) {
              console.error('Error removing audio from contest:', err);
            }
          }
        }
        
        dispatch(showToast({ color: "success", msg: "Contest updated successfully" }));
      }
      
      // Refresh data
      await Promise.all([fetchChallenges(), canSeeContests ? fetchContests() : Promise.resolve()]);
      setEditModalOpen(false);
      setSelectedEvent(null);
      setEditFormData({});
      setSelectedAudioIds([]);
      setOriginalAudioIds([]);
    } catch (error) {
      console.error("Error updating:", error);
      dispatch(showToast({ 
        color: "error", 
        msg: error.response?.data?.message || "Failed to update" 
      }));
    } finally {
      setSaving(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setEditModalOpen(false);
    setSelectedEvent(null);
    setEditFormData({});
    setSelectedAudioIds([]);
    setOriginalAudioIds([]);
  };

  // Open Add Challenge modal with optional date
  const handleOpenAddModal = (date = null) => {
    const startDate = date ? dayjs(date) : dayjs();
    setAddFormData({
      title: "",
      categoryId: "",
      startDate: startDate.format("YYYY-MM-DDTHH:mm"),
      endDate: startDate.add(7, "day").format("YYYY-MM-DDTHH:mm"),
    });
    setAddModalOpen(true);
  };

  // Handle add form change
  const handleAddFormChange = (field, value) => {
    setAddFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle add challenge
  const handleAddChallenge = async () => {
    if (!addFormData.title?.trim()) {
      dispatch(showToast({ color: "error", msg: "Title is required" }));
      return;
    }
    if (!addFormData.categoryId) {
      dispatch(showToast({ color: "error", msg: "Category is required" }));
      return;
    }
    if (!addFormData.startDate || !addFormData.endDate) {
      dispatch(showToast({ color: "error", msg: "Start and end dates are required" }));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: addFormData.title.trim(),
        categoryId: addFormData.categoryId,
        startDate: new Date(addFormData.startDate).toISOString(),
        endDate: new Date(addFormData.endDate).toISOString(),
      };

      await AxiosInstance.post("/category/challenge", payload);
      dispatch(showToast({ color: "success", msg: "Challenge created successfully" }));
      
      // Refresh data
      await fetchChallenges();
      setAddModalOpen(false);
      setAddFormData({ title: "", categoryId: "", startDate: "", endDate: "" });
    } catch (error) {
      console.error("Error creating challenge:", error);
      dispatch(showToast({ 
        color: "error", 
        msg: error.response?.data?.message || "Failed to create challenge" 
      }));
    } finally {
      setSaving(false);
    }
  };

  // Get date from mouse position on the calendar grid
  const getDateFromMousePosition = useCallback((clientX, clientY) => {
    if (!gridRef.current) return null;
    
    const gridRect = gridRef.current.getBoundingClientRect();
    const relativeX = clientX - gridRect.left;
    const relativeY = clientY - gridRect.top;
    
    // Calculate which column (day of week)
    const col = Math.floor(relativeX / cellWidth);
    if (col < 0 || col > 6) return null;
    
    // Calculate which row (week)
    let cumulativeHeight = 0;
    let weekRow = -1;
    const numWeeks = Math.ceil(calendarDays.length / 7);
    
    for (let i = 0; i < numWeeks; i++) {
      const rowHeight = getRowHeight(i);
      if (relativeY >= cumulativeHeight && relativeY < cumulativeHeight + rowHeight) {
        weekRow = i;
        break;
      }
      cumulativeHeight += rowHeight;
    }
    
    if (weekRow === -1) return null;
    
    // Get the day index
    const dayIndex = weekRow * 7 + col;
    if (dayIndex < 0 || dayIndex >= calendarDays.length) return null;
    
    return calendarDays[dayIndex].date;
  }, [cellWidth, calendarDays, getRowHeight]);

  // Start resize drag
  const handleResizeStart = useCallback((e, event, handle) => {
    e.preventDefault();
    e.stopPropagation();
    
    const originalStart = dayjs(event.startDate).startOf("day");
    const originalEnd = dayjs(event.endDate).startOf("day");
    
    setDragState({
      type: handle === 'start' ? 'resize-start' : 'resize-end',
      event,
      originalStart,
      originalEnd,
      currentStart: originalStart,
      currentEnd: originalEnd,
    });
  }, []);

  // Start move drag
  const handleMoveStart = useCallback((e, event) => {
    // Don't start move if we're clicking on a resize handle
    if (e.target.classList.contains('gantt-bar-resize-handle')) return;
    
    e.preventDefault();
    
    const originalStart = dayjs(event.startDate).startOf("day");
    const originalEnd = dayjs(event.endDate).startOf("day");
    const duration = originalEnd.diff(originalStart, 'day');
    
    setDragState({
      type: 'move',
      event,
      originalStart,
      originalEnd,
      currentStart: originalStart,
      currentEnd: originalEnd,
      duration,
    });
  }, []);

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!dragState) return;
    
    const targetDate = getDateFromMousePosition(e.clientX, e.clientY);
    if (!targetDate) return;
    
    if (dragState.type === 'resize-start') {
      // Resizing start - can't go past end date
      const newStart = targetDate.startOf("day");
      if (newStart.isBefore(dragState.currentEnd) || newStart.isSame(dragState.currentEnd, "day")) {
        setDragState(prev => ({
          ...prev,
          currentStart: newStart,
        }));
      }
    } else if (dragState.type === 'resize-end') {
      // Resizing end - can't go before start date
      const newEnd = targetDate.startOf("day");
      if (newEnd.isAfter(dragState.currentStart) || newEnd.isSame(dragState.currentStart, "day")) {
        setDragState(prev => ({
          ...prev,
          currentEnd: newEnd,
        }));
      }
    } else if (dragState.type === 'move') {
      // Moving - maintain duration
      const newStart = targetDate.startOf("day");
      const newEnd = newStart.add(dragState.duration, 'day');
      setDragState(prev => ({
        ...prev,
        currentStart: newStart,
        currentEnd: newEnd,
      }));
    }
  }, [dragState, getDateFromMousePosition]);

  // Handle drag end - update via API
  const handleDragEnd = useCallback(async () => {
    if (!dragState) return;
    
    const { event, originalStart, originalEnd, currentStart, currentEnd } = dragState;
    
    // Check if dates actually changed
    const startChanged = !currentStart.isSame(originalStart, "day");
    const endChanged = !currentEnd.isSame(originalEnd, "day");
    
    if (!startChanged && !endChanged) {
      setDragState(null);
      return;
    }
    
    // Update via API
    try {
      if (event.type === "challenge") {
        const payload = {
          startDate: currentStart.toISOString(),
          endDate: currentEnd.endOf("day").toISOString(),
        };
        await AxiosInstance.put(`/category/challenge/${event.rawId}`, payload);
        dispatch(showToast({ color: "success", msg: "Challenge dates updated" }));
      } else {
        const payload = {
          id: event.rawId,
          votingStartDate: currentStart.toISOString(),
          votingEndDate: currentEnd.endOf("day").toISOString(),
          // Update submission end date to 15 minutes before voting starts
          endDate: currentStart.subtract(15, 'minute').toISOString(),
        };
        await AxiosInstance.put("/contest", payload);
        dispatch(showToast({ color: "success", msg: "Contest voting dates updated" }));
      }
      
      // Refresh data
      await Promise.all([fetchChallenges(), canSeeContests ? fetchContests() : Promise.resolve()]);
    } catch (error) {
      console.error("Error updating dates:", error);
      dispatch(showToast({ 
        color: "error", 
        msg: error.response?.data?.message || "Failed to update dates" 
      }));
    }
    
    setDragState(null);
  }, [dragState, dispatch, fetchChallenges, fetchContests]);

  // Global mouse event listeners for drag
  useEffect(() => {
    if (!dragState) return;
    
    const onMouseMove = (e) => handleDragMove(e);
    const onMouseUp = () => handleDragEnd();
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragState, handleDragMove, handleDragEnd]);

  // Calculate ghost bar position and dimensions
  const getGhostBarStyle = useCallback(() => {
    if (!dragState || !gridRef.current) return null;
    
    const { currentStart, currentEnd, event } = dragState;
    const gridRect = gridRef.current.getBoundingClientRect();
    
    // Find the start day index
    const startDayIndex = calendarDays.findIndex(d => 
      d.date.startOf("day").isSame(currentStart, "day")
    );
    
    if (startDayIndex === -1) return null;
    
    // Find the end day index
    let endDayIndex = calendarDays.findIndex(d => 
      d.date.startOf("day").isSame(currentEnd, "day")
    );
    
    // If end is beyond the calendar, use last day
    if (endDayIndex === -1) {
      if (currentEnd.isAfter(calendarDays[calendarDays.length - 1].date)) {
        endDayIndex = calendarDays.length - 1;
      } else {
        return null;
      }
    }
    
    // Get the slot for this event
    const slot = eventSlots.get(event.id) || 0;
    const statusInfo = getEventStatus(event);
    
    // Calculate position for first segment
    const weekRow = Math.floor(startDayIndex / 7);
    const startCol = startDayIndex % 7;
    const endCol = Math.min(endDayIndex % 7 + (Math.floor(endDayIndex / 7) === weekRow ? 0 : 6 - startCol), 6);
    
    // Calculate cumulative height to this row
    let topOffset = 0;
    for (let i = 0; i < weekRow; i++) {
      topOffset += getRowHeight(i);
    }
    
    const left = gridRect.left + startCol * cellWidth + 4;
    const width = (Math.min(endDayIndex, weekRow * 7 + 6) - startDayIndex + 1) * cellWidth - 8;
    const top = gridRect.top + topOffset + DATE_HEADER_HEIGHT + slot * (ROW_HEIGHT + ROW_GAP);
    
    return {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: '36px',
      backgroundColor: hexToRgba(statusInfo.color, 0.7),
      borderColor: statusInfo.borderColor,
      border: `2px solid ${statusInfo.borderColor}`,
      borderRadius: '4px',
      pointerEvents: 'none',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      padding: '0 10px',
      gap: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    };
  }, [dragState, calendarDays, cellWidth, eventSlots, getEventStatus, getRowHeight]);

  return (
    <PageLayout
      title="Calendar"
      actions={
        <Tooltip title="Open Full Screen" arrow>
          <IconButton
            onClick={() => navigate(routePaths.CALENDAR_PRINT)}
            sx={{
              backgroundColor: "#142C5C",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#1e3a6e",
              },
            }}
          >
            <OpenInFull />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ padding: "0", height: "100%" }}>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress sx={{ color: "#142C5C" }} />
        </Box>
      )}

      {!loading && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "20px",
            boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
            width: "100%",
            minWidth: 0,
            transition: "width 0.3s ease",
          }}
        >
          <CalendarToolbar goToPrevQuarter={goToPrevQuarter} goToNextQuarter={goToNextQuarter} getQuarterLabel={getQuarterLabel} showChallenges={showChallenges} setShowChallenges={setShowChallenges} showContests={showContests} setShowContests={setShowContests} showKaraoke={showKaraoke} setShowKaraoke={setShowKaraoke} showDance={showDance} setShowDance={setShowDance} showRegistrations={showRegistrations} setShowRegistrations={setShowRegistrations} goToToday={goToToday} />

          <CalendarGrid gridRef={gridRef} calendarDays={calendarDays} getMonthStartsInWeek={getMonthStartsInWeek} getRowHeight={getRowHeight} showRegistrations={showRegistrations} registrationsByDate={registrationsByDate} setSelectedDemographicsDate={setSelectedDemographicsDate} setDemographicsModalOpen={setDemographicsModalOpen} handleOpenAddModal={handleOpenAddModal} currentQuarter={currentQuarter} visibleEvents={visibleEvents} getEventBarSegments={getEventBarSegments} eventSlots={eventSlots} getEventStatus={getEventStatus} cellWidth={cellWidth} dragState={dragState} handleBarClick={handleBarClick} handleMoveStart={handleMoveStart} handleResizeStart={handleResizeStart} />
        </Box>
      )}

      {/* Ghost bar preview during drag */}
      {dragState && (
        <div style={getGhostBarStyle()}>
          {dragState.event.logo && (
            <img 
              src={dragState.event.logo} 
              alt="" 
              style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 3 }} 
            />
          )}
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
            {dragState.event.title}
          </span>
          <span style={{ fontSize: 11, color: '#64748b', marginLeft: 'auto' }}>
            {dragState.currentStart.format("MMM D")} - {dragState.currentEnd.format("MMM D")}
          </span>
        </div>
      )}

      <EditEventDialog editModalOpen={editModalOpen} handleCloseModal={handleCloseModal} selectedEvent={selectedEvent} editFormData={editFormData} handleFormChange={handleFormChange} allAudios={allAudios} selectedAudioIds={selectedAudioIds} setSelectedAudioIds={setSelectedAudioIds} saving={saving} setSaving={setSaving} dispatch={dispatch} fetchChallenges={fetchChallenges} fetchContests={fetchContests} handleSave={handleSave} />

      <AddChallengeDialog addModalOpen={addModalOpen} setAddModalOpen={setAddModalOpen} addFormData={addFormData} handleAddFormChange={handleAddFormChange} categories={categories} handleAddChallenge={handleAddChallenge} saving={saving} />

      <DemographicsDialog demographicsModalOpen={demographicsModalOpen} setDemographicsModalOpen={setDemographicsModalOpen} selectedDemographicsDate={selectedDemographicsDate} setSelectedDemographicsDate={setSelectedDemographicsDate} getDemographicsForDate={getDemographicsForDate} />
      </Box>
    </PageLayout>
  );
};

export default Calendar;
